import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { warehouseAPI } from '../../api/warehouses';
import { Warehouse } from '../../types/entities';
import { Plus, Edit, Trash2, Eye, Search } from 'lucide-react';
import toast from 'react-hot-toast';

export const WarehouseList: React.FC = () => {
  const { t } = useTranslation();
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const navigate = useNavigate();

  useEffect(() => {
    loadWarehouses();
  }, [search, statusFilter]);

  const loadWarehouses = async () => {
    try {
      setLoading(true);
      const data = await warehouseAPI.getAll({
        status: statusFilter || undefined,
        search: search || undefined,
      });
      setWarehouses(data);
    } catch (error) {
      toast.error(t('warehouses.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('warehouses.deleteConfirm'))) return;

    try {
      await warehouseAPI.delete(id);
      toast.success(t('warehouses.deleteSuccess'));
      loadWarehouses();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('warehouses.deleteError'));
    }
  };

  // Debounce search
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  if (loading && warehouses.length === 0) {
    return <div className="text-center py-10">{t('common.loading')}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">{t('warehouses.title')}</h1>
        <button
          onClick={() => navigate('/warehouses/new')}
          className="btn-primary flex items-center"
        >
          <Plus className="w-5 h-5 mr-2" />
          {t('warehouses.addNew')}
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder={t('warehouses.searchPlaceholder')}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>
          <div className="w-48">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input-field"
            >
              <option value="">{t('common.all')}</option>
              <option value="ACTIVE">{t('common.active')}</option>
              <option value="INACTIVE">{t('common.inactive')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <table className="w-full">
          <thead className="border-b">
            <tr className="text-left">
              <th className="pb-3 table-header">{t('warehouses.code')}</th>
              <th className="pb-3 table-header">{t('warehouses.name')}</th>
              <th className="pb-3 table-header">{t('warehouses.address')}</th>
              <th className="pb-3 table-header">{t('warehouses.contact')}</th>
              <th className="pb-3 table-header">{t('common.status')}</th>
              <th className="pb-3 table-header">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {warehouses.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-8 text-center text-gray-500">
                  {t('common.noData')}
                </td>
              </tr>
            ) : (
              warehouses.map((warehouse) => (
                <tr key={warehouse.id} className="border-b last:border-0">
                  <td className="py-3 font-mono text-sm font-medium text-primary-600">
                    {warehouse.code}
                  </td>
                  <td className="py-3 font-medium">{warehouse.name}</td>
                  <td className="py-3 text-gray-600">{warehouse.address || '-'}</td>
                  <td className="py-3">
                    {warehouse.contactPerson ? (
                      <div>
                        <div className="font-medium">{warehouse.contactPerson}</div>
                        {warehouse.contactPhone && (
                          <div className="text-sm text-gray-500">{warehouse.contactPhone}</div>
                        )}
                      </div>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="py-3">
                    <span
                      className={`px-2 py-1 rounded text-sm ${
                        warehouse.status === 'ACTIVE'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {warehouse.status === 'ACTIVE' ? t('common.active') : t('common.inactive')}
                    </span>
                  </td>
                  <td className="py-3">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => navigate(`/warehouses/${warehouse.id}`)}
                        className="p-2 text-gray-600 hover:bg-gray-50 rounded"
                        title={t('common.view')}
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => navigate(`/warehouses/${warehouse.id}/edit`)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                        title={t('common.edit')}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(warehouse.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                        title={t('common.delete')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
